import { createApi } from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { ApiResponse } from '@/app/common/types/common';
import {
  AddProviderKeyRequest,
  DeleteProviderKeyData,
  DeleteProviderKeyRequest,
  GetAiUserSettingsData,
  GetAiUserSettingsRequest,
  GetProviderKeyRequest,
  GetProviderModelsRequest,
  GetProvidersRequest,
  ModelOption,
  ProviderKeyDetail,
  ProviderOption,
  SetProviderKeyStatusData,
  SetProviderKeyStatusRequest,
  UpdateProviderKeyRequest,
  UpsertAiUserSettingsData,
  UpsertAiUserSettingsRequest,
  VerifyUserApiKeysData
} from '@/app/Settings/types/aiSettingsApiTypes';
import { getBaseApiPath } from '@/app/common/api/config';

const normalizeOptionsArray = <T,>(response: ApiResponse<T[]> | T[]) => {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.data) ? response.data : [];
};

const aiSettingsApi = createApi({
  reducerPath: 'aiSettingsApi',
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['AiSettings'],
  endpoints: build => ({
    getAiUserSettings: build.query<
      GetAiUserSettingsData,
      GetAiUserSettingsRequest | void
    >({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/get',
        method: 'POST',
        body: body ?? {}
      }),
      transformResponse: (response: ApiResponse<GetAiUserSettingsData>) => {
        return response.data;
      },
      providesTags: ['AiSettings']
    }),
    getProviders: build.query<ProviderOption[], GetProvidersRequest>({
      query: () => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/providers',
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ProviderOption[]> | ProviderOption[]) =>
        normalizeOptionsArray<ProviderOption>(response)
    }),
    getProviderModels: build.query<ModelOption[], GetProviderModelsRequest>({
      query: providerId => ({
        url: `/ai_binesh/mizfa_tools/v1/user_settings/provider-models/${providerId}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ModelOption[]> | ModelOption[]) =>
        normalizeOptionsArray<ModelOption>(response)
    }),
    addProviderKey: build.mutation<string, AddProviderKeyRequest>({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/add-provider-key',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<string>) => response.data,
      invalidatesTags: ['AiSettings']
    }),
    getProviderKey: build.query<ProviderKeyDetail, GetProviderKeyRequest>({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/get-provider-key',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<ProviderKeyDetail>) => response.data
    }),
    updateProviderKey: build.mutation<ProviderKeyDetail, UpdateProviderKeyRequest>({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/update-provider-key',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<ProviderKeyDetail>) => response.data,
      invalidatesTags: ['AiSettings']
    }),
    deleteProviderKey: build.mutation<DeleteProviderKeyData, DeleteProviderKeyRequest>({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/delete-provider-key',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<DeleteProviderKeyData>) => response.data,
      invalidatesTags: ['AiSettings']
    }),
    setActiveProviderKey: build.mutation<
      SetProviderKeyStatusData,
      SetProviderKeyStatusRequest
    >({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/set-active-provider-key',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<SetProviderKeyStatusData>) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          aiSettingsApi.util.updateQueryData('getAiUserSettings', {}, draft => {
            if (!draft?.provider_keys) return;
            const targetKey = draft.provider_keys.find(item => item.id === arg.id);
            if (targetKey) targetKey.is_active = true;
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    }),
    setInactiveProviderKey: build.mutation<
      SetProviderKeyStatusData,
      SetProviderKeyStatusRequest
    >({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/set-inactive-provider-key',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<SetProviderKeyStatusData>) => response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          aiSettingsApi.util.updateQueryData('getAiUserSettings', {}, draft => {
            if (!draft?.provider_keys) return;
            const targetKey = draft.provider_keys.find(item => item.id === arg.id);
            if (targetKey) targetKey.is_active = false;
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    }),
    verifyUserApiKeys: build.mutation<VerifyUserApiKeysData, void>({
      query: () => ({
        url: '/ai_binesh/mizfa_tools/v1/llm/user-gemini/verify-keys',
        method: 'POST',
        body: {}
      }),
      transformResponse: (response: ApiResponse<VerifyUserApiKeysData>) =>
        response.data,
      invalidatesTags: ['AiSettings']
    }),
    upsertAiUserSettings: build.mutation<
      UpsertAiUserSettingsData,
      UpsertAiUserSettingsRequest
    >({
      query: body => ({
        url: '/ai_binesh/mizfa_tools/v1/user_settings/upsert',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<UpsertAiUserSettingsData>) =>
        response.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          aiSettingsApi.util.updateQueryData('getAiUserSettings', {}, draft => {
            if (!draft) return;
            draft.settings = {
              ...draft.settings,
              enable_binesh_ai: arg.enable_binesh_ai,
              enable_user_ai: arg.enable_user_ai
            };
          })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    })
  })
});

export const {
  useGetAiUserSettingsQuery,
  useLazyGetAiUserSettingsQuery,
  useGetProvidersQuery,
  useLazyGetProviderModelsQuery,
  useAddProviderKeyMutation,
  useLazyGetProviderKeyQuery,
  useUpdateProviderKeyMutation,
  useDeleteProviderKeyMutation,
  useSetActiveProviderKeyMutation,
  useSetInactiveProviderKeyMutation,
  useVerifyUserApiKeysMutation,
  useUpsertAiUserSettingsMutation
} = aiSettingsApi;

export default aiSettingsApi;
